import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatINR } from "@/lib/format";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell,
} from "recharts";
import { Users, Briefcase, Building2, BadgeCheck, AlertTriangle, Trophy, ClipboardList, ArrowRight, CalendarRange } from "lucide-react";
import LateComingWidget from "@/components/LateComingWidget";
import ChecklistDashboardTile from "@/components/ChecklistDashboardTile";
import BirthdayWidget from "@/components/BirthdayWidget";
import HRTilesWidget from "@/components/HRTilesWidget";
import DormancyWidget from "@/components/DormancyWidget";
import AnniversaryWidget from "@/components/AnniversaryWidget";
import SalarySummaryWidget from "@/components/SalarySummaryWidget";
import PayrollCycleWidget from "@/components/PayrollCycleWidget";

const PALETTE = ["#BE1019", "#105330", "#A7744A", "#888780", "#D2AD78", "#8FC3A3", "#E0989D"];

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [delegation, setDelegation] = useState(null);
  const [fyList, setFyList] = useState([]);
  const [activeFy, setActiveFy] = useState(null);
  // "" = last 6 months (default).  Otherwise a financial_years.id.
  const [fyId, setFyId] = useState("");

  useEffect(() => {
    api.get("/financial-years").then(({ data }) => setFyList(data || [])).catch(() => {});
    api.get("/financial-years/active").then(({ data }) => setActiveFy(data)).catch(() => {});
    api.get("/delegation/stats").then(({ data }) => setDelegation(data)).catch(() => {});
  }, []);

  useEffect(() => {
    const url = fyId ? `/dashboard/summary?fy_id=${fyId}` : "/dashboard/summary";
    api.get(url).then(({ data }) => setData(data));
  }, [fyId]);

  if (!data) return <div className="text-sm text-slate-500">Loading dashboard...</div>;
  const { kpis, by_unit, outflow_chart, outflow_keys, dept_chart, top_ot, exceptions, outflow_total, fy: selectedFy } = data;

  const cards = [
    { label: "Total Employees", value: kpis.total_employees, icon: Users, topBorder: "#BE1019" },
    { label: "Active (Working)", value: kpis.active_employees, icon: BadgeCheck, accent: "text-success", topBorder: "#105330" },
    { label: "Total Active CTC", value: formatINR(kpis.total_active_ctc), icon: Briefcase, accent: "brand-text", topBorder: "#A7744A" },
    { label: "Manufacturing Units", value: kpis.units, icon: Building2, topBorder: "#888780" },
  ];

  return (
    <div data-testid="dashboard-page" className="space-y-6">
      <div>
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Overview</div>
        <h1 className="text-2xl sm:text-3xl font-bold text-foreground tracking-tight" style={{ fontFamily: "Chivo" }}>
          Welcome back, {user?.name}
        </h1>
        <p className="text-sm text-muted-foreground mt-1">Workforce snapshot &amp; payroll trends.</p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        {cards.map((c) => {
          const Icon = c.icon;
          return (
            <Card
              key={c.label}
              className="border-border shadow-sm rounded-xl overflow-hidden"
              style={{ borderTop: `3px solid ${c.topBorder}` }}
              data-testid={`stat-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <CardContent className="p-4 sm:p-5">
                <div className="flex items-start justify-between">
                  <div className="text-[10px] sm:text-[11px] uppercase tracking-wider text-muted-foreground font-medium">
                    {c.label}
                  </div>
                  <Icon className="h-4 w-4 text-muted-foreground" />
                </div>
                <div className={`mt-2 text-xl sm:text-2xl font-bold ${c.accent || "text-foreground"}`}
                     style={{ fontFamily: "Chivo" }}>
                  {c.value}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Phase B Session 7 — HR quick tiles (New Joiners / Left / Zero-Attendance) */}
      <HRTilesWidget />

      {/* Phase B Session 8.2 — Dormancy Radar (30/60/90 day + Ghost Suspects) */}
      <DormancyWidget />

      {/* Phase 6 — Compliance Checklist tile (auto-hides if no data) */}
      <ChecklistDashboardTile />

      {/* Birthdays this week (auto-hides if none) */}
      <BirthdayWidget />

      {/* Work Anniversaries this week (auto-hides if none) */}
      <AnniversaryWidget />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Outflow chart */}
        <Card className="lg:col-span-2 border-border shadow-sm">
          <CardContent className="p-4 sm:p-5">
            <div className="flex items-start justify-between mb-3 flex-wrap gap-2">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 flex items-center gap-1.5">
                  <CalendarRange className="h-3 w-3" />
                  {fyId && selectedFy ? selectedFy.label : "Last 6 months"}
                  {fyId && selectedFy?.status === "closed" && (
                    <span className="ml-1 bg-slate-200 text-slate-700 text-[9px] uppercase px-1 py-0.5 rounded">Closed</span>
                  )}
                </div>
                <div className="font-semibold text-slate-900 text-sm" style={{ fontFamily: "Chivo" }}>
                  Salary outflow by unit
                </div>
                {fyId && outflow_total > 0 && (
                  <div className="text-[11px] text-slate-500 mt-0.5">
                    FY total: <b className="brand-text tabular-nums">{formatINR(outflow_total)}</b>
                  </div>
                )}
              </div>
              <Select value={fyId || "_recent"} onValueChange={(v) => setFyId(v === "_recent" ? "" : v)}>
                <SelectTrigger className="h-8 w-44 text-xs" data-testid="fy-picker">
                  <SelectValue placeholder="Range" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_recent">Last 6 months</SelectItem>
                  {activeFy?.id && !fyList.some((f) => f.id === activeFy.id) && (
                    <SelectItem key={activeFy.id} value={activeFy.id}>
                      {activeFy.label} (current)
                    </SelectItem>
                  )}
                  {fyList.map((f) => (
                    <SelectItem key={f.id} value={f.id}>
                      {f.label}{f.status === "closed" ? " · closed" : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {outflow_chart.length === 0 ? (
              <div className="h-64 flex items-center justify-center text-sm text-slate-400">
                No approved payroll runs yet
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={outflow_chart}>
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `₹${(v/1000).toFixed(0)}k`} />
                  <Tooltip formatter={(v) => formatINR(v)} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  {outflow_keys.map((k, i) => (
                    <Bar key={k} dataKey={k} stackId="a" fill={PALETTE[i % PALETTE.length]} />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* Dept-wise donut */}
        <Card className="border-border shadow-sm">
          <CardContent className="p-4 sm:p-5">
            <div className="text-[10px] uppercase tracking-widest text-slate-500">Latest run</div>
            <div className="font-semibold text-slate-900 text-sm mb-3" style={{ fontFamily: "Chivo" }}>
              Department-wise payout
            </div>
            {dept_chart.length === 0 ? (
              <div className="h-60 flex items-center justify-center text-sm text-slate-400">No data</div>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie data={dept_chart} dataKey="value" nameKey="department"
                       cx="50%" cy="50%" innerRadius={50} outerRadius={90} paddingAngle={2}>
                    {dept_chart.map((_, i) => (
                      <Cell key={i} fill={PALETTE[i % PALETTE.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v) => formatINR(v)} />
                  <Legend wrapperStyle={{ fontSize: 10 }} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Top OT earners */}
        <Card className="border-border shadow-sm">
          <CardContent className="p-4 sm:p-5">
            <div className="flex items-center gap-2 mb-3">
              <Trophy className="h-4 w-4 brand-text" />
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500">Latest run</div>
                <div className="font-semibold text-slate-900 text-sm" style={{ fontFamily: "Chivo" }}>
                  Top 5 OT earners
                </div>
              </div>
            </div>
            {top_ot.length === 0 ? (
              <div className="text-sm text-slate-400 py-8 text-center">No OT recorded yet</div>
            ) : (
              <div className="space-y-2">
                {top_ot.map((e, i) => (
                  <div key={i} className="flex items-center justify-between text-sm border-b border-slate-100 pb-2 last:border-0">
                    <div>
                      <div className="font-medium text-slate-900">{e.name}</div>
                      <div className="text-[11px] text-slate-500 font-mono">
                        {e.punch_id} · {Math.floor(e.ot_minutes/60)}h {e.ot_minutes%60}m
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-semibold brand-text tabular-nums">{formatINR(e.ot_earning)}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Exceptions */}
        <Card className="border-border shadow-sm">
          <CardContent className="p-4 sm:p-5">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500">Action required</div>
                <div className="font-semibold text-slate-900 text-sm" style={{ fontFamily: "Chivo" }}>
                  Exceptions
                </div>
              </div>
            </div>
            {exceptions.length === 0 ? (
              <div className="text-sm text-slate-400 py-8 text-center">All clear ✓</div>
            ) : (
              <div className="space-y-2">
                {exceptions.map((e, i) => (
                  <div key={i} className="flex items-center justify-between text-sm border-b border-slate-100 pb-2 last:border-0">
                    <div>
                      <div className="font-medium text-slate-900">{e.name}</div>
                      <div className="text-[11px] text-slate-500 font-mono">{e.punch_id}</div>
                    </div>
                    <div className="text-[10px] uppercase tracking-wider text-amber-700 font-medium">
                      {e.type === "missing_ctc" ? "Missing CTC" : "Long Holiday"}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Phase 5D — Late Coming Widget (HR / Super Admin / Unit Manager) */}
      {["super_admin", "hr", "unit_manager"].includes(user?.role) && (
        <LateComingWidget />
      )}

      {/* Phase 17.5 — Salary Summary Matrix (super_admin/hr only) */}
      {["super_admin", "hr"].includes(user?.role) && (
        <SalarySummaryWidget />
      )}

      {/* Rev. 2026-08 — Monthly Payroll Cycle progress card (super_admin/hr only) */}
      {["super_admin", "hr"].includes(user?.role) && (
        <PayrollCycleWidget />
      )}

      {/* Delegation Overview — super_admin/HR */}
      {delegation?.all && (
        <Card className="border-border shadow-sm" data-testid="delegation-overview-card">
          <CardContent className="p-4 sm:p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <ClipboardList className="h-4 w-4 brand-text" />
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500">Delegation Overview</div>
                  <div className="font-semibold text-slate-900 text-sm" style={{ fontFamily: "Chivo" }}>
                    Tasks across the organisation
                  </div>
                </div>
              </div>
              <Link
                to="/delegation/tasks"
                className="text-xs font-medium brand-text hover:underline inline-flex items-center gap-1"
                data-testid="delegation-overview-link"
              >
                View all <ArrowRight className="h-3 w-3" />
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <StatTile label="Total" value={delegation.all.total || 0} accent="slate" testid="overview-total" />
              <StatTile label="Open" value={delegation.all.open || 0} accent="brand" testid="overview-open" />
              <StatTile label="Overdue" value={delegation.all.overdue || 0} accent="red" testid="overview-overdue" />
              <StatTile label="Completed" value={delegation.all.completed || 0} accent="emerald" testid="overview-completed" />
            </div>
            {delegation.all.by_status && Object.keys(delegation.all.by_status).length > 0 && (
              <div className="mt-4 flex flex-wrap gap-1.5" data-testid="overview-by-status">
                {Object.entries(delegation.all.by_status).map(([s, n]) => (
                  <span key={s} className="text-[11px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 border border-slate-200">
                    {s.replace("_", " ")} · <b>{n}</b>
                  </span>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function StatTile({ label, value, accent = "slate", testid }) {
  const tone = {
    slate: "border-slate-100 bg-slate-50/40 text-slate-700",
    brand: "border-red-100 bg-red-50/40 brand-text",
    red: "border-red-100 bg-red-50/40 text-red-700",
    emerald: "border-emerald-100 bg-emerald-50/40 text-emerald-700",
  }[accent];
  return (
    <div className={`rounded-md border ${tone} px-3 py-3 text-center`} data-testid={testid}>
      <div className="text-2xl font-bold">{value}</div>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-1">{label}</div>
    </div>
  );
}
